import React, { forwardRef } from 'react'

import styled from 'styled-components'

const StyledDiv = styled.div<{
  $align: 'baseline' | 'center' | 'end' | 'start' | 'stretch'
  $direction: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  $flex?: React.CSSProperties['flex']
  $flexWrap?: React.CSSProperties['flexWrap']
  $gap: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
  $height?: React.CSSProperties['height']
  $justify: 'start' | 'center' | 'end' | 'space-around' | 'space-between' | 'stretch'
  $margin?: React.CSSProperties['margin']
  $maxHeight?: React.CSSProperties['maxHeight']
  $maxWidth?: React.CSSProperties['maxWidth']
  $minHeight?: React.CSSProperties['minHeight']
  $minWidth?: React.CSSProperties['minWidth']
  $padding?: React.CSSProperties['padding']
  $position?: React.CSSProperties['position']
  $width?: React.CSSProperties['width']
}>`
  display: flex;
  flex-direction: ${({ $direction }) => $direction};
  align-items: ${({ $align }) =>
    $align === 'start'
      ? 'flex-start'
      : $align === 'end'
        ? 'flex-end'
        : $align === 'stretch'
          ? 'stretch'
          : $align === 'baseline'
            ? 'baseline'
            : 'center'};
  justify-content: ${({ $justify }) =>
    $justify === 'start'
      ? 'flex-start'
      : $justify === 'end'
        ? 'flex-end'
        : $justify === 'space-between'
          ? 'space-between'
          : $justify === 'space-around'
            ? 'space-around'
            : $justify === 'stretch'
              ? 'stretch'
              : 'center'};
  gap: ${({ $gap }) => $gap * 4}px;
  ${({ $flex }) => ($flex ? `flex: ${$flex};` : '')};
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height }) => $height ?? 'auto'};
  ${({ $maxHeight }) => ($maxHeight ? `max-height: ${$maxHeight};` : '')};
  max-width: ${({ $maxWidth }) => $maxWidth ?? 'auto'};
  min-height: ${({ $minHeight }) => $minHeight ?? 'auto'};
  min-width: ${({ $minWidth }) => $minWidth ?? 'auto'};
  ${({ $margin }) => ($margin ? `margin: ${$margin};` : '')};
  ${({ $padding }) => ($padding ? `padding: ${$padding};` : '')};
  ${({ $flexWrap }) => ($flexWrap ? `flex-wrap: ${$flexWrap};` : '')};
  ${({ $position }) => ($position ? `position: ${$position};` : '')};
`

export type FlexProps = {
  align?: 'baseline' | 'center' | 'end' | 'start' | 'stretch'
  children?: React.ReactNode
  className?: string
  dataTestId?: string
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  flex?: React.CSSProperties['flex']
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
  height?: React.CSSProperties['height']
  justify?: 'center' | 'end' | 'space-around' | 'space-between' | 'start' | 'stretch'
  margin?: React.CSSProperties['margin']
  maxHeight?: React.CSSProperties['maxHeight']
  maxWidth?: React.CSSProperties['maxWidth']
  minHeight?: React.CSSProperties['minHeight']
  minWidth?: React.CSSProperties['minWidth']
  padding?: React.CSSProperties['padding']
  position?: React.CSSProperties['position']
  style?: React.CSSProperties
  width?: React.CSSProperties['width']
  wrap?: React.CSSProperties['flexWrap']
}

const Flex = forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      align = 'center',
      children,
      className,
      dataTestId,
      direction = 'row',
      flex,
      gap = 0,
      height,
      justify = 'start',
      margin,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      padding,
      position,
      style,
      width,
      wrap,
    }: FlexProps,
    ref
  ) => {
    return (
      <StyledDiv
        ref={ref}
        $align={align}
        className={className}
        data-testid={dataTestId}
        $direction={direction}
        $flex={flex}
        $flexWrap={wrap}
        $gap={gap}
        $height={height}
        $justify={justify}
        $margin={margin}
        $maxHeight={maxHeight}
        $maxWidth={maxWidth}
        $minHeight={minHeight}
        $minWidth={minWidth}
        $padding={padding}
        $position={position}
        style={style}
        $width={width}
      >
        {children}
      </StyledDiv>
    )
  }
)

export default Flex
